/* eslint-disable @typescript-eslint/no-unused-vars */
import { IconX } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import NewinputField from "../../../components/NewInput";
import { useOrderLabelPrintMutation } from "../../../apis/orderApis";
import toast from "react-hot-toast";
import ButtonStyled from "../../../components/common/ButtonStyled";

type LabelPrintModalProps = {
  open: boolean;
  onClose: () => void;
  order: any;
  inPlace?: boolean;
};

export default function LabelPrintModal({
  open,
  onClose,
  order,
  inPlace = false,
}: LabelPrintModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      noOfPieces: 1,
    },
  });

  const is3PLStatus = order?.status === "FORWARD_TO_3PL";
  const [is3PLEnabled, setIs3PLEnabled] = useState<boolean>(is3PLStatus);

  const selectedSystem = is3PLEnabled ? "3pl" : "default";

  const { mutate, isPending } = useOrderLabelPrintMutation(order?.id ?? "");

  const handleGenerate = (data: any) => {
    const payload = {
      ...data,
      system: selectedSystem,
    };
    mutate(payload, {
      onSuccess: () => {
        toast.success("Label generated successfully");
        onClose();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to generate label");
        console.log(error, "error");
      },
    });
  };

  if (!open) return null;

  if (inPlace) {
    return (
      <div className="bg-white border border-slate-200/80 shadow-lg rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-slate-100 bg-white">
          <h3 className="text-base font-bold text-[#212121]">Label Print</h3>
          <div className="flex items-center gap-2">
            <ButtonStyled
              type="button"
              onClick={handleSubmit(handleGenerate)}
              isPending={isPending}
              variant="outline"
              value="Generate"
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-slate-100 transition-colors text-slate-700 bg-slate-50 cursor-pointer"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* Body — compact without extra space */}
        <div className="p-4 bg-white space-y-3">
          <div className="flex items-center justify-between bg-white border border-[#E8E8E8] rounded-xl px-4 py-2.5 shadow-sm">
            <div>
              <p className="text-xs font-semibold text-[#212121]">
                {is3PLEnabled ? "3PL Label Print" : "Default Label Print"}
              </p>
              <p className="text-[10px] text-[#212121]/60">
                {is3PLEnabled ? "Switch to Default" : "Switch to 3PL (Forwarded AWB)"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIs3PLEnabled((prev) => !prev)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                is3PLEnabled ? "bg-[#FBD037]" : "bg-[#D8D8D8]"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                  is3PLEnabled ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          <div className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
            <div className="grid grid-cols-2 gap-3 relative z-10">
              <NewinputField
                className="flex items-center gap-2"
                title="Size"
                name="size"
                value="4 x 6 inches"
                register={register}
                disabled
              />
              <NewinputField
                className="flex items-center gap-2"
                title="Pieces"
                name="noOfPieces"
                type="number"
                placeholder=""
                register={register}
                error={errors?.noOfPieces}
                validation={{
                  required: "No. of pieces is required",
                  min: { value: 1, message: "At least 1 piece is required" },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      <div className="bg-white rounded-2xl xl:w-5/12 lg:w-6/12 w-11/12 h-[calc(100vh-2rem)] flex flex-col overflow-hidden shadow-2xl relative">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#d2d2d2] bg-white shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#212121]">Label Print</h3>
          </div>
          <div className="flex items-center gap-2">
            <ButtonStyled
              type="button"
              onClick={handleSubmit(handleGenerate)}
              isPending={isPending}
              variant="outline"
              value="Generate"
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 bg-[#F2F2F7] cursor-pointer"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="w-full flex-1 overflow-y-auto p-6 bg-white">
          <div className="space-y-4">
            {/* Toggle Switch */}
            <div className="flex items-center justify-between bg-white border border-[#E8E8E8] rounded-xl px-4 py-3 shadow-sm">
              <div>
                <p className="text-sm font-semibold text-[#212121]">
                  {is3PLEnabled ? "3PL Label Print" : "Default Label Print"}
                </p>
                <p className="text-xs text-[#212121]/60">
                  {is3PLEnabled
                    ? "Switch to Default Label Print"
                    : "Switch to 3PL (Forwarded AWB)"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIs3PLEnabled((prev) => !prev)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  is3PLEnabled ? "bg-[#FBD037]" : "bg-[#D8D8D8]"
                }`}
                role="switch"
                aria-checked={is3PLEnabled}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                    is3PLEnabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Fields */}
            <div className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-4">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="grid grid-cols-2 gap-4 relative z-10">
                <NewinputField
                  className="flex items-center gap-2"
                  title="Size"
                  name="size"
                  value="4 x 6 inches"
                  register={register}
                  disabled
                />
                <NewinputField
                  className="flex items-center gap-2"
                  title="No. of Pieces"
                  name="noOfPieces"
                  type="number"
                  placeholder=""
                  register={register}
                  error={errors?.noOfPieces}
                  validation={{
                    required: "No. of pieces is required",
                    min: { value: 1, message: "At least 1 piece is required" },
                    max: {
                      value: 9999,
                      message: "Maximum allowed is 9999 pieces",
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
